/* tslint:disable:max-classes-per-file  */
import * as _4 from 'lodash4';
import { APGenericSlotServant } from '../servants/ap_generic_slot_servant';
import { ap_course_status_type } from '../strategies/ap_course_status_strategy';
import { APCourse } from './ap_course_builder';
import { APCourseGroup } from './ap_course_group_builder';
import { IAPPlannedCourseMeta } from './ap_planned_course_builder';
import { APPlannedCourse } from './ap_planned_course_builder';
import {
  APProgramSlot,
  APProgramSlotMeta,
  CourseGroupProgramSlot,
  CourseGroupProgramSlotMeta,
  IAPProgramSlotMeta,
  NCRProgramSlot,
  SingleCourseProgramSlot,
  SingleCourseProgramSlotMeta,
  WildcardProgramSlot
} from './ap_program_slot_builder';
import { PlannedCourseGroup } from './planned_course_group_builder';

export interface IGenericSlotFlagComment {
  comment?: string;
  id?: number;
  hoverTemplate?: string;
}

export type slotType = 'COURSE' | 'GROUP' | 'NCR' | 'WILDCARD';

export type TSlotProgramSlot =
  | (SingleCourseProgramSlot & IAPProgramSlotMeta)
  | (CourseGroupProgramSlot & IAPProgramSlotMeta)
  | (WildcardProgramSlot & IAPProgramSlotMeta)
  | (NCRProgramSlot & IAPProgramSlotMeta);

export class Slot {
  uuid?: string;
  title: string;
  type: slotType;
  programSlot: TSlotProgramSlot;
  disableSlot?: boolean;
  showMarkAsMetMenu?: boolean = false;
  showMarkAsUnmetMenu?: boolean = false;
}

export class CourseSlot extends Slot {
  type: 'COURSE';
  plannedCourse: APPlannedCourse & IAPPlannedCourseMeta;
  course: APCourse;
  termId: number;
  status: ap_course_status_type;
  isDragEnabled: boolean;
  isPlanned: boolean;
  credits: number;
  isPlannedSomewhere: boolean;
  hoverTemplate?: string;
  isCourseFlagged?: boolean;
  flagComment?: IGenericSlotFlagComment;
  isLockedByAdvisor?: boolean;
  isLockedByStatus?: boolean;
  programSlot: SingleCourseProgramSlot & IAPProgramSlotMeta;
  isPlannedInCurrentGroup?: boolean;
  courseOfferedProblemMessage?: string;
  showGradeBadge?: boolean;
  shouldShowAddToTermMenu?: boolean;
  isMinGradeMet?: boolean;
  isCourseCompleted?: boolean;

  constructor(template: CourseSlot) {
    super();
    _4.merge(this, template);
    this.uuid = this.uuid = APGenericSlotServant.uuid(this as CourseSlot);
  }
}

export class NCRBase extends Slot {
  instructions: string;
  plannedCourseGroup: APCourseGroup;
  isDragEnabled: boolean;
  isPlanned: boolean;
  termId: number;
}

export class CourseGroupBase extends NCRBase {
  plannedCourse?: APPlannedCourse & IAPPlannedCourseMeta;
  isPlaceHolder: boolean;
  status: ap_course_status_type;
  isLockedByStatus: boolean;
  hoverTemplate?: string;
  isCourseFlagged?: boolean;
  flagComment?: IGenericSlotFlagComment;
  isLockedByAdvisor: boolean;
}

export class WildcardSlot extends CourseGroupBase {
  type: 'WILDCARD';
  course?: APCourse;
  genericSlots: CourseSlot[];
  credits: number;
  isPlannedInCurrentGroup: boolean;
  programSlot: WildcardProgramSlot & IAPProgramSlotMeta;

  constructor(template: WildcardSlot) {
    super();
    _4.merge(this, template);
    this.uuid = this.uuid = APGenericSlotServant.uuid(this as WildcardSlot);
  }
}

export class NCRSlot extends NCRBase {
  type: 'NCR';
  status: 'DEFAULT' | 'PLANNED';
  programSlot: NCRProgramSlot & IAPProgramSlotMeta;
  constructor(template: NCRSlot) {
    super();
    _4.merge(this, template);
    this.uuid = this.uuid = APGenericSlotServant.uuid(this as NCRSlot);
  }
}

export class CourseGroupSlot extends CourseGroupBase {
  type: 'GROUP';
  courses: APCourse[];
  firstCourse: APCourse;
  genericSlots: CourseSlot[];
  min_credits: number;
  max_credits: number;
  programSlot: CourseGroupProgramSlot & IAPProgramSlotMeta;
  isPlannedInCurrentGroup: boolean;
  showGradeBadge?: boolean;
  shouldShowAddToTermMenu?: boolean;

  constructor(template: CourseGroupSlot) {
    super();
    _4.merge(this, template);
    this.uuid = this.uuid = APGenericSlotServant.uuid(this as CourseGroupSlot);
  }
}

export type APCRGenericSlot = CourseGroupSlot | CourseSlot | WildcardSlot;

export type APGenericSlot = APCRGenericSlot | NCRSlot;

export class APGenericSlotBuilder {
  uuid: Slot['uuid'];
  type: Slot['type'];
  title: Slot['title'];
  disableSlot?: Slot['disableSlot'];
  courses?: APCourse[];
  course?: APCourse;
  plannedCourse: APPlannedCourse & IAPPlannedCourseMeta;
  plannedCourseGroup: APCourseGroup;
  termId: number;
  instructions?: string;
  programSlot: APProgramSlot & IAPProgramSlotMeta;
  isPlaceHolder: boolean;
  status: ap_course_status_type;
  isLockedByStatus: boolean;
  isDragEnabled: boolean;
  firstCourse: APCourse;
  genericSlots?: CourseSlot[];
  isPlanned: boolean;
  credits: number;
  min_credits?: number;
  max_credits?: number;
  isPlannedSomewhere?: boolean;
  isPlannedInCurrentGroup?: boolean;
  hoverTemplate?: string;
  isCourseFlagged: boolean;
  flagComment: IGenericSlotFlagComment;
  isLockedByAdvisor: boolean;
  showGradeBadge?: boolean;
  shouldShowAddToTermMenu?: boolean;
  isMinGradeMet?: boolean;

  constructor(template: APGenericSlot) {
    _4.merge(this, template);
    this.uuid = this.uuid = APGenericSlotServant.uuid(this as APGenericSlot);
  }

  static build(slot: CourseSlot): CourseSlot;
  static build(slot: NCRSlot): NCRSlot;
  static build(slot: WildcardSlot): WildcardSlot;
  static build(slot: CourseGroupSlot): CourseGroupSlot;

  static build(slot: APGenericSlot): APGenericSlot {
    if (APGenericSlotServant.isCourseGroupSlot(slot)) {
      return new CourseGroupSlot(slot);
    } else if (APGenericSlotServant.isWildcardSlot(slot)) {
      return new WildcardSlot(slot);
    } else if (APGenericSlotServant.isCourseSlot(slot)) {
      return new CourseSlot(slot);
    } else {
      return new NCRSlot(slot);
    }
  }
}



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-academic-planner/src/global/builders/ap_generic_slot_builder.ts